import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { fleetListService } from 'services';
import { CustomButton, CustomInput, Spinner } from 'components';
import { FleetList as FleetListItem, CreateFleetListRequest } from 'interfaces';

import './FleetList.scss';

export const FleetList = () => {
  const [fleetList, setFleetList] = useState<FleetListItem[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const formik = useFormik<CreateFleetListRequest>({
    initialValues: { imo: '', name: '', show: true },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        await fleetListService.create({
          imo: values.imo?.trim() || undefined,
          name: values.name?.trim() || undefined,
          show: values.show,
        });
        await loadFleetList();
        resetForm();
      } catch (e) {
        console.error('Error creating fleet list item:', e);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const loadFleetList = async () => {
    setIsListLoading(true);
    try {
      const response = await fleetListService.getAll();
      if (response) {
        setFleetList(response);
      }
    } catch (e) {
      console.error('Error loading fleet list:', e);
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    loadFleetList();
  }, []);

  const handleToggleShow = async (id: number, currentShow: boolean) => {
    try {
      await fleetListService.update(id, { show: !currentShow });
      await loadFleetList();
    } catch (e) {
      console.error('Error updating fleet list item:', e);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await fleetListService.delete(id);
        await loadFleetList();
      } catch (e) {
        console.error('Error deleting fleet list item:', e);
      }
    }
  };

  return (
    <div className="fleet-list-container">
      <div className="fleet-list__header-box">
        <h6 className="fleet-list__header">Fleet List</h6>
      </div>

      <form onSubmit={formik.handleSubmit} className="fleet-list__form">
        <div className="fleet-list__form-inputs">
          <CustomInput
            id="imo"
            name="imo"
            label="IMO"
            placeholder="Enter IMO number"
            width="100%"
            value={formik.values.imo || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <CustomInput
            id="name"
            name="name"
            label="Vessel Name"
            placeholder="Enter vessel name"
            width="100%"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className="fleet-list__form-actions">
          <CustomButton
            type="submit"
            text="Add to Fleet List"
            width="200px"
            disabled={isSubmitting || (!formik.values.name && !formik.values.imo)}
            backgroundColor="#10b981"
          />
        </div>
      </form>

      <div className="fleet-list__fields">
        <div className="fleet-list__field">
          <p className="fleet-list__text">IMO</p>
        </div>
        <div className="fleet-list__field">
          <p className="fleet-list__text">Vessel Name</p>
        </div>
        <div className="fleet-list__field">
          <p className="fleet-list__text">Actions</p>
        </div>
      </div>

      <div className="fleet-list__data-container">
        {fleetList.length !== 0 &&
          !isListLoading &&
          fleetList.map(({ id, imo, name, show }) => (
            <div key={id} className="fleet-list__data-box">
              <p className="fleet-list__data-item">{imo || '-'}</p>
              <p className="fleet-list__data-item">{name || '-'}</p>
              <div className="fleet-list__data-item">
                <CustomButton
                  text={show ? 'In Service' : 'Out of Service'}
                  width="150px"
                  height="35px"
                  backgroundColor={show ? '#10b981' : '#ef4444'}
                  onClick={() => handleToggleShow(id, show)}
                />
                <CustomButton
                  text="Delete"
                  width="100px"
                  height="35px"
                  backgroundColor="#ef4444"
                  onClick={() => handleDelete(id)}
                />
              </div>
            </div>
          ))}
        {!fleetList.length && !isListLoading && (
          <div className="fleet-list__empty">Your fleet list is empty</div>
        )}
        {isListLoading && (
          <div className="fleet-list__spinner-box">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};
